import { useState, useEffect, useRef } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import useMenuBack from '../../hooks/useMenuBack'
import { useI18n } from '../../i18n'
import { useProfileStore, useUserStore } from '../../stores'
import { profileAPI } from '../../services/api'
import MyInsuranceView from '../../components/MyInsuranceView'
import { buildBenefitsView, recheckAvailableAt } from '../../utils/benefitsView'
import { eligibilityCopy } from '../../utils/eligibilityCopy'
import { eligibilityAnswer, eligibilityKey, loadedResultKey, limitedResultFor, limitNoticeKey, limitNoticeParams } from '../../utils/eligibilityLimit'
import { fmtRelative } from '../../components/EligibilityBadge'
import { MY_INSURANCE_SCREEN } from '../../config/insuranceLevel1'

/**
 * MyInsurance — PROD-581 (insurance level 1), /profile/insurance/benefits. "My insurance": the
 * eligibility answer Orella already stores, as plan, dates, per-visit copays, deductible and
 * out-of-pocket (what is left), coinsurance and network, always as an estimate with its source.
 *
 * Behind src/config/insuranceLevel1.js MY_INSURANCE_SCREEN (OFF): while it is off, or while the
 * UI language has no approved text for the screen (es until Dr. Rami approves), anyone who
 * reaches this route is sent to Profile -> Insurance.
 *
 * Re-check (V-2, Alex 2026-10-05): offered once per 30 days after the stored check (PROD-936 PAT-2, was
 * once per week: the server reuses a stored result of the same coverage for 30 days anyway). It runs the same
 * practice-NPI check as Profile -> Insurance's Re-check, on the coverage SAVED on the profile (this
 * screen has no form), and reads the answer through the one reader (eligibilityLimit.js): a
 * server limit answer keeps the stored result, and one for coverage the stored result may not
 * belong to shows no verdict (Q-C5). The 30-day rule here is the screen's; the server's own limit
 * is PROD-589's.
 */
export default function MyInsurance() {
  const { t, hasText, language } = useI18n()
  const navigate = useNavigate()
  const goBack = useMenuBack()
  const { profile, updateProfile } = useProfileStore()
  const userId = useUserStore((s) => s.user?.id)
  const [tier, setTier] = useState('primary')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const aliveRef = useRef(true)
  useEffect(() => () => { aliveRef.current = false }, [])

  if (!MY_INSURANCE_SCREEN || !hasText('myInsurance.title')) return <Navigate to="/profile/insurance" replace />

  const ins = profile?.insurance && typeof profile.insurance === 'object' ? profile.insurance : {}
  const secondary = ins.last_eligibility_secondary && typeof ins.last_eligibility_secondary === 'object' ? ins.last_eligibility_secondary : null
  const shownTier = tier === 'secondary' && secondary ? 'secondary' : 'primary'
  const eligibility = shownTier === 'secondary' ? secondary : (ins.last_eligibility || null)
  const view = buildBenefitsView(eligibility)
  const noInsurance = shownTier === 'primary' && (ins.selfPay === true || !ins.provider)
  const availableAt = shownTier === 'primary' ? recheckAvailableAt(view.checkedAt) : null

  const onRecheck = async () => {
    if (!userId || busy || shownTier !== 'primary') return
    setBusy(true)
    setNotice(null)
    try {
      const resp = await profileAPI.runPracticeEligibility(userId)
      const current = useProfileStore.getState().profile?.insurance || {}
      const shown = limitedResultFor(eligibilityAnswer(resp), eligibilityKey(current), loadedResultKey(current))
      if (shown.notice) {
        const key = limitNoticeKey(shown.notice)
        if (aliveRef.current && key && hasText(key)) setNotice(t(key, limitNoticeParams(shown.notice, language === 'es' ? 'es-US' : 'en-US', (iso) => fmtRelative(iso, t))))
      } else if (shown.eligibility) {
        updateProfile({ insurance: { ...current, last_eligibility: shown.eligibility } })
      }
    } catch (e) {
      console.warn('[MY_INSURANCE] recheck failed:', e?.message || e)
    } finally {
      if (aliveRef.current) setBusy(false)
    }
  }

  return (
    <MyInsuranceView
      view={view}
      copy={eligibilityCopy(eligibility, language)}
      tier={shownTier}
      onTier={setTier}
      hasSecondary={!!secondary}
      noInsurance={noInsurance}
      recheck={{ availableAt, busy, notice, onRecheck: shownTier === 'primary' ? onRecheck : null }}
      onBack={() => goBack()}
      onOpenInsurance={() => navigate('/profile/insurance')}
    />
  )
}
